// MACHINERY V2 (Stroje) — the shared hard-delete flow (MDEL-FE).
//
// Spec: MACHINE_DELETE_SPEC.md §7.4 / §7.5. ONE implementation for BOTH
// surfaces (fleet kebab + profile decommission banner); they differ only in
// what happens AFTER a successful delete, which is the `onDeleted` callback the
// caller supplies. A second copy is how a confirm text or a fail-closed branch
// ends up correct on one surface and wrong on the other.
//
// >>> THE CLIENT IS A MIRROR. THE SERVER IS THE AUTHORITY. <<<
// The eligibility read below decides only whether to bother the user with a
// confirm dialog. It NEVER stands in for the server check: the delete request is
// always sent, the server re-runs the full check on it, and its refusal is
// rendered here exactly like a pre-flight refusal. So a stale "eligible" read
// cannot turn into a delete the server would refuse — it turns into a refusal
// the user sees. The mirror can only ever refuse EARLIER than the server; it can
// never permit.
//
// AND IT IS NOT THE GATE EITHER. Whether the delete ACTION renders at all is
// decided by usePermission(Machinery).canAdmin inside buildFleetActions, which
// costs no request. Nothing here runs until the user has already chosen to
// delete, so no eligibility call is made on page load, per machine, or to paint
// a menu.
//
// THREE THINGS GUARD THE FLOW, AND THEY GUARD DIFFERENT FAILURES:
//  1. `busyRef` — synchronous re-entry guard. Blocks a second delete starting
//     while one is running. A ref, not state, because setState is async and a
//     double-click lands inside one tick.
//  2. `useV2Confirm` — mutual exclusion with the OTHER v2 confirmations
//     (review finding 1). busyRef cannot see a restore, because restore is a
//     different code path; without this gate a slow eligibility read could
//     replace an already-open restore dialog in place and turn a click already
//     in motion into a hard delete of a different machine.
//  3. `AbortController` — hard stop. Without it a hung request never releases
//     busyRef and delete stays dead for the page's lifetime (review finding 2).
// A fourth outcome is the SERVER's guard, not ours: BOTH endpoints are rate
// limited per authenticated user and answer 429 with an empty body, each under
// its OWN policy — the eligibility GET
// (CustomerMachinesController.cs:395) and the permanent DELETE (:422), the
// second deliberately tighter because a repeated DELETE of the same machine is
// not a legitimate UI probe. Both paths get the throttle message, because
// falling through to "the check failed" tells a throttled user the wrong cause
// AND invites the retry that keeps them throttled.
//
// NO LIMITS ARE QUOTED HERE ON PURPOSE. Both are config-overridable
// (`MachineDelete:RateLimit:EligibilityPerMinute` /
// `:PermanentDeletePerMinute`, MachineDeleteRateLimiter.cs:40-44); the
// in-code numbers are only fallbacks, so a figure repeated here would describe
// no particular environment. The API file names the defaults with their lines.
//
// (If a 429 ever stops being DETECTED, look at the limiter's per-policy
// OnRejected — MachineDeleteRateLimiter.cs:83-87 — which is what pins the
// status to 429 instead of ASP.NET's default 503, and which wins over the
// shared RejectionStatusCode because it runs after it. It is NOT the shared
// assignment in NameplateRateLimiter.cs any more; that inheritance still
// exists but these two endpoints stopped relying on it.)
// Every one of the three paths out — refused, blocked, errored, timed out,
// succeeded — releases busyRef and returns `state` to "idle".
//
// FRICTION: TWO DELIBERATE CLICKS ON TWO DIFFERENTLY-PLACED DANGER TARGETS,
// with the machine NAMED in between — not a typed confirmation. Reasoning:
// (1) the server guard means an accepted delete can only ever remove a machine
// with no reports, documents, nameplate, requests, work orders or live
// connection, so the blast radius of a mis-click is a record-free shell;
// (2) the owner's stated problem is that removing a mistyped machine is
// IMPOSSIBLE, so ceremony added here works against the feature's purpose;
// (3) the real defence against deleting the WRONG machine is identification,
// not repetition, which is why the name + serial are in the dialog and in the
// success toast.
//
// NO SERVER BEHAVIOUR IS ASSERTED IN THIS FILE. The Machinery endpoints are
// being written concurrently on module/machinery in GoToStockServer-machinery
// and do not exist yet, so every remark about responses describes what THIS
// code does with them, per the frozen contract in the spec — not a controller
// line anyone has read.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useCallback, useRef, useState } from "react";

import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { getMachineDisplayName } from "../../../../shared/utils/machineUtils";
import { Machine } from "../../models/models";
import customerMachineryApi from "../../api/customerMachineryApi";
import { setError, setSuccess } from "../../customerMachinerySlice";
import { toastErrorMessage } from "../feedback/MachineryToastHost";
import { useV2Confirm } from "../feedback/useV2Confirm";
import { buildBlockedMessage } from "./machineDeleteBlockers";

/**
 * CLIENT HARD STOP — a backstop against a hung request, not a responsiveness
 * budget.
 *
 * MEASURED, NOT ASSUMED: the three peer checks run together under ONE
 * `Task.WhenAll` (MachineDeleteService.cs:230-245), and each peer client pins
 * its own 8 s budget — ServicingClient.cs:15, MaintenanceClient.cs:15 and
 * GatewayClient.cs:15 all hold `TimeSpan.FromSeconds(8)`. The eligibility worst
 * case is therefore ~8 s plus the local DB queries: PARALLEL, not serial.
 *
 * An earlier version of this comment justified the same number from a serial
 * fan-out at 5-10 s per peer, i.e. ~30 s. That premise was simply wrong. The
 * constant survives unchanged because ~4x headroom over the real worst case is
 * exactly what a hang backstop wants: it can only ever fire on a genuine hang,
 * never on a slow check that would have completed. Aborting those would fail
 * the feature instead of protecting it.
 */
const REQUEST_TIMEOUT_MS = 32000;

/** "checking" = eligibility read; "deleting" = the delete request after
 *  confirmation. Both mean busy; the split only picks the label. */
export type MachineDeleteState = "idle" | "checking" | "deleting";

export interface MachineDeleteControls {
  /** Opens the delete flow. `onDeleted` runs only after a successful delete. */
  requestDelete: (machine: Machine, onDeleted: () => void) => void;
  /** Drives the disabled/busy affordance on whatever the user clicked. */
  deleteState: MachineDeleteState;
  /** Convenience for callers that only need "is something running". */
  deleteBusy: boolean;
}

export const useMachineDelete = (): MachineDeleteControls => {
  const dispatch = useAppDispatch();
  const confirmV2 = useV2Confirm();
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");
  const busyRef = useRef(false);
  const [deleteState, setDeleteState] = useState<MachineDeleteState>("idle");

  /** THE single release point. Every exit calls it — including the ones that
   *  look like they cannot fail. */
  const finish = useCallback(() => {
    busyRef.current = false;
    setDeleteState("idle");
  }, []);

  const requestDelete = useCallback(
    (machine: Machine, onDeleted: () => void) => {
      if (busyRef.current) return;
      busyRef.current = true;
      setDeleteState("checking");

      const name = getMachineDisplayName(machine);
      // Leading space belongs to the caller: §8's confirmText appends
      // {{serial}} straight onto the closing quote of {{name}}.
      const serial = machine.serialNumber
        ? " " +
          translate("myMachinery:v2.delete.serialSuffix", { sn: machine.serialNumber })
        : "";

      // `timedOut` is tracked here rather than read off the rejection because
      // core/api.ts:72-77 rebuilds EVERY non-bot rejection as `{message, code}`
      // from `error.response.data` — an aborted request has no `response`, so
      // both fields arrive undefined and axios's own ERR_CANCELED is gone by the
      // time a caller sees it.
      const runWithTimeout = async <T>(
        call: (signal: AbortSignal) => Promise<T>
      ): Promise<{ ok: true; value: T } | { ok: false; timedOut: boolean; error: any }> => {
        const controller = new AbortController();
        let timedOut = false;
        const timer = setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, REQUEST_TIMEOUT_MS);
        try {
          return { ok: true, value: await call(controller.signal) };
        } catch (error) {
          return { ok: false, timedOut, error };
        } finally {
          clearTimeout(timer);
        }
      };

      const confirmDelete = () =>
        confirmV2({
          title: translate("myMachinery:v2.delete.confirmTitle"),
          text: translate("myMachinery:v2.delete.confirmText", { name, serial }),
          confirmButton: translate("myMachinery:v2.delete.confirmButton"),
          // common.json has NO top-level `cancel` key — the real one is
          // buttons.cancel (the restore handlers carry the same note after it
          // rendered the literal string "cancel").
          closeButton: tCommon("buttons.cancel"),
          // ConfirmationDialog's severity union is "neutral" | "error" — there
          // is no "danger" member. "error" is the red/destructive styling.
          severity: "error",
          onClose: finish,
          // ConfirmationProvider neither awaits nor catches this callback, so
          // everything below is self-contained (fleet review F6's fix).
          onConfirm: async () => {
            setDeleteState("deleting");
            const res = await runWithTimeout((signal) =>
              customerMachineryApi.deleteMachinePermanently(machine.id, signal)
            );
            if (!res.ok) {
              // NEITHER MESSAGE SAYS THE DELETE FAILED. The request left this
              // client and an abort here does not abort the server, so on this
              // path the outcome is genuinely unknown — see the copy note on
              // `timedOut` in the locales.
              dispatch(
                setError(
                  res.timedOut
                    ? translate("myMachinery:v2.delete.timedOut")
                    : toastErrorMessage(res.error, translate("myMachinery:v2.delete.failed"))
                )
              );
              finish();
              return;
            }
            if (res.value.kind === "throttled") {
              // Rate-limited (20/min per user). NOT a failure of the delete and
              // NOT a refusal — telling the user either would send them
              // straight back into the limiter.
              dispatch(setError(translate("myMachinery:v2.delete.throttled")));
              finish();
              return;
            }
            if (res.value.kind === "refused") {
              // The server re-ran the check and refused. Same enumerated
              // payload, same renderer as the pre-flight refusal.
              dispatch(
                setError(buildBlockedMessage(res.value.eligibility.blockers, translate))
              );
              finish();
              return;
            }
            dispatch(setSuccess(translate("myMachinery:v2.delete.success", { name })));
            finish();
            onDeleted();
          },
        });

      const run = async () => {
        const res = await runWithTimeout((signal) =>
          customerMachineryApi.getMachineDeleteEligibility(machine.id, signal)
        );
        if (!res.ok) {
          // Nothing was sent to the delete endpoint on this path, so saying so
          // is a statement about THIS code, not about the server.
          dispatch(
            setError(
              res.timedOut
                ? translate("myMachinery:v2.delete.checkTimedOut")
                : toastErrorMessage(res.error, translate("myMachinery:v2.delete.checkFailed"))
            )
          );
          finish();
          return;
        }
        if (res.value.kind === "throttled") {
          dispatch(setError(translate("myMachinery:v2.delete.throttled")));
          finish();
          return;
        }
        if (res.value.eligibility.canDelete !== true) {
          dispatch(
            setError(buildBlockedMessage(res.value.eligibility.blockers, translate))
          );
          finish();
          return;
        }
        // REFUSED, NOT DROPPED. Another v2 confirmation opened while we were
        // waiting; replacing it in place is the wrong-machine-delete bug, so we
        // bail — but visibly, and busyRef is released so the user can simply
        // close that dialog and click Delete again.
        if (!confirmDelete()) {
          dispatch(setError(translate("myMachinery:v2.delete.confirmBusy")));
          finish();
        }
      };

      void run();
    },
    [confirmV2, dispatch, translate, tCommon, finish]
  );

  return { requestDelete, deleteState, deleteBusy: deleteState !== "idle" };
};

export default useMachineDelete;
